class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  connectedCallback() {
    if (this.controller) return;
    this.header = this.closest('.header-wrapper');
    this.navigation = this.closest('.header__inline-menu');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };

    this.syncDisclosures();
    this.mainDetailsToggle.addEventListener('pointerenter', (event) => {
      if (!this.desktop.matches || event.pointerType !== 'mouse') return;
      this.open();
    }, options);
    this.mainDetailsToggle.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || this.editorSelected) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        if (!(this.keyboardFocus && this.contains(document.activeElement))) this.close();
      }, 150);
    }, options);
    this.addEventListener('focusin', () => {
      window.clearTimeout(this.closeTimer);
      this.keyboardFocus = document.activeElement.matches(':focus-visible');
    }, options);
    this.addEventListener('pointerdown', () => { this.keyboardFocus = false; }, options);
    this.addEventListener('keydown', (event) => {
      this.keyboardFocus = true;
      if (event.key !== 'Escape') return;
      const disclosure = event.target.closest('details[open]');
      if (!disclosure || !this.contains(disclosure)) return;
      event.preventDefault();
      event.stopPropagation();
      if (disclosure === this.mainDetailsToggle) this.close();
      else disclosure.open = false;
      this.syncDisclosures();
      disclosure.querySelector('summary').focus({ preventScroll: true });
    }, options);
    // The global Escape keyup handler must not close a second disclosure.
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') event.stopPropagation();
    }, { ...options, capture: true });
    // Nested dropdowns also need ARIA updates after a Theme Editor reload.
    this.addEventListener('toggle', (event) => {
      if (event.target !== this.mainDetailsToggle) this.syncDisclosures();
    }, { ...options, capture: true });
    this.desktop.addEventListener('change', () => {
      if (!this.desktop.matches) this.close();
    }, options);
    window.addEventListener('resize', () => {
      if (this.mainDetailsToggle.open) this.updatePosition();
    }, options);
    this.addEventListener('shopify:block:select', () => {
      this.editorSelected = true;
      if (this.desktop.matches) this.open();
    }, options);
    this.addEventListener('shopify:block:deselect', () => {
      this.editorSelected = false;
      this.close();
    }, options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.focusTimer);
    this.updateHeaderState();
  }

  syncDisclosures() {
    this.querySelectorAll('details').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', String(details.open));
      if (summary.nextElementSibling?.id) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      }
    });
  }

  updateHeaderState() {
    if (this.header) {
      this.header.preventHide = Boolean(this.header.querySelector('header-menu > details[open]'));
    }
  }

  updatePosition() {
    if (!this.header || !this.summary) return;
    const bottom = this.header.getBoundingClientRect().bottom;
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(bottom)}px`);
    this.mainDetailsToggle.style.setProperty('--header-menu-gap', `${Math.max(0, bottom - this.summary.getBoundingClientRect().bottom) + 2}px`);
  }

  closeOtherMenus() {
    this.navigation?.querySelectorAll('header-menu').forEach((menu) => {
      if (menu !== this && menu.mainDetailsToggle?.open) menu.close();
    });
  }

  open() {
    window.clearTimeout(this.closeTimer);
    this.closeOtherMenus();
    this.updatePosition();
    this.mainDetailsToggle.open = true;
    this.syncDisclosures();
    this.updateHeaderState();
  }

  close() {
    window.clearTimeout(this.closeTimer);
    // Return focus before hiding a panel that another menu is replacing.
    if (this.content.contains(document.activeElement)) {
      this.summary?.focus({ preventScroll: true });
    }
    super.close();
    this.updateHeaderState();
  }

  onFocusOut() {
    window.clearTimeout(this.focusTimer);
    this.focusTimer = window.setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.editorSelected) this.close();
    });
  }

  onToggle() {
    this.syncDisclosures();
    if (this.mainDetailsToggle.open) {
      this.closeOtherMenus();
      this.updatePosition();
    }
    this.updateHeaderState();
  }
}

customElements.define('header-menu', HeaderMenu);
